(() => {
  'use strict';
  if (customElements.get('floyd-store-locator-consent')) return;

  class FloydStoreLocatorConsent extends HTMLElement {
    connectedCallback() {
      this.required = JSON.parse(this.dataset.requiredServices || '[]');
      this.placeholder = this.querySelector('[data-placeholder]');
      this.container = this.querySelector('[data-frame-container]');
      this.status = this.querySelector('[data-status]');
      this.settingsButton = this.querySelector('[data-settings]');
      this.decisions = new Map();
      this.onConsent = (event) => {
        if (event.detail?.event !== 'consent_status') return;
        if (event.detail.action === 'onDenyAllServices') {
          this.required.forEach(name => this.decisions.set(name, false));
        } else {
          // Consume the event payload immediately, before an SDK snapshot can lag behind it.
          this.required.forEach(name => {
            if (typeof event.detail[name] === 'boolean') this.decisions.set(name, event.detail[name]);
          });
        }
        this.renderConsent();
      };
      this.onReady = () => this.readConsent();
      this.onPageShow = () => this.readConsent();
      this.openSettings = () => {
        try {
          if (!window.UC_UI?.isInitialized()) throw new Error('CMP unavailable');
          window.UC_UI.showSecondLayer();
          this.status.textContent = '';
        } catch (_) {
          this.status.textContent = this.dataset.unavailable;
        }
      };
      window.addEventListener('UC_UI_INITIALIZED', this.onReady);
      window.addEventListener('ucEvents', this.onConsent);
      window.addEventListener('pageshow', this.onPageShow);
      this.settingsButton.addEventListener('click', this.openSettings);
      this.readConsent();
    }

    disconnectedCallback() {
      window.removeEventListener('UC_UI_INITIALIZED', this.onReady);
      window.removeEventListener('ucEvents', this.onConsent);
      window.removeEventListener('pageshow', this.onPageShow);
      this.settingsButton?.removeEventListener('click', this.openSettings);
      this.container?.replaceChildren();
      this.frame = null;
    }

    readConsent() {
      // Missing CMP or missing service fails closed. Never substitute a category-wide decision.
      this.decisions.clear();
      try {
        if (window.UC_UI?.isInitialized()) {
          const services = window.UC_UI.getServicesBaseInfo();
          this.required.forEach(name => {
            const matches = services.filter(service => service.name === name);
            this.decisions.set(name, matches.length === 1 && matches[0].consent?.status === true);
          });
        }
      } catch (_) { /* Keep the local placeholder if CMP cannot provide valid decisions. */ }
      this.renderConsent();
    }

    renderConsent() {
      const allowed = this.required.length === 3 && this.required.every(name => this.decisions.get(name) === true);
      if (!allowed) {
        // Removing markup alone would leave Maps running; withdrawal also reloads the page.
        const wasLoaded = Boolean(this.frame);
        const restoreFocus = this.frame && this.frame.contains(document.activeElement);
        this.container.replaceChildren();
        this.frame = null;
        this.placeholder.hidden = false;
        if (restoreFocus) this.settingsButton.focus();
        // Maps has no complete teardown API. Reload after withdrawal to stop all widget activity.
        if (wasLoaded) window.location.reload();
        return;
      }
      if (this.frame) return;
      const language = ['de', 'en', 'fr'].includes(this.dataset.language) ? this.dataset.language : 'en';
      const widgetId = this.dataset.widgetId;
      if (!/^[a-f0-9]+$/.test(widgetId || '')) return;
      this.frame = document.createElement('div');
      this.frame.id = 'storepoint-widget';
      this.frame.setAttribute('aria-label', this.dataset.frameTitle);
      const script = document.createElement('script');
      script.src = 'https://widget.storepoint.co/embed.js';
      script.async = true;
      script.onload = () => {
        if (!this.frame || !this.required.every(name => this.decisions.get(name) === true)) return;
        try { new StorepointWidget(widgetId, '#storepoint-widget', {language}); }
        catch (_) { this.status.textContent = this.dataset.loadError; }
      };
      script.onerror = () => { this.status.textContent = this.dataset.loadError; };
      this.container.appendChild(this.frame);
      this.container.appendChild(script);
      this.placeholder.hidden = true;
      this.status.textContent = '';
    }
  }
  customElements.define('floyd-store-locator-consent', FloydStoreLocatorConsent);
})();
